import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _typeof from "@babel/runtime/helpers/typeof";
import React, { useId, useMemo, useState } from 'react';
import { LTCopyToClipboard, LTModal, LTUnderlineNav } from '@lambdatestincprivate/lt-components';
import { ScriptTerminal } from './CustomScriptModal';
import { KeyValueTable } from './KeyValueTable';
import styles from './steps.module.css';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/** kaneai SecretIcon entity="variable" — the {x} glyph (quoted paths). */
var VariableGlyph = function VariableGlyph() {
  return /*#__PURE__*/_jsxs("svg", {
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    "aria-hidden": "true",
    children: [/*#__PURE__*/_jsx("path", {
      d: "M10.8344 5.875H10.7776C10.3627 5.875 9.96855 6.05723 9.69858 6.37387L6.28595 10.3761C6.01596 10.6928 5.62182 10.875 5.20696 10.875H5.15002M5.86063 5.875H6.74575C7.06302 5.875 7.34182 6.08641 7.42899 6.39306L8.55559 10.357C8.64277 10.6636 8.92156 10.875 9.23883 10.875H10.124",
      stroke: "var(--lt-icon-primary)",
      strokeWidth: "1.36054",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/_jsx("path", {
      d: "M4.33333 3H3.66667C3.31304 3 2.97391 3.11706 2.72386 3.32544C2.47381 3.53381 2.33333 3.81643 2.33333 4.11111V6.88889C2.33333 7.18357 2.19286 7.46619 1.94281 7.67456C1.69276 7.88294 1.35362 8 1 8C1.35362 8 1.69276 8.11706 1.94281 8.32544C2.19286 8.53381 2.33333 8.81643 2.33333 9.11111V11.8889C2.33333 12.5 2.93333 13 3.66667 13H4.33333",
      stroke: "var(--lt-icon-primary)",
      strokeWidth: "1.25",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    }), /*#__PURE__*/_jsx("path", {
      d: "M11.6667 13H12.3334C12.687 13 13.0262 12.8829 13.2762 12.6746C13.5263 12.4662 13.6667 12.1836 13.6667 11.8889V9.11111C13.6667 8.5 14.2667 8 15.0001 8C14.6465 8 14.3073 7.88294 14.0573 7.67456C13.8072 7.46619 13.6667 7.18357 13.6667 6.88889V4.11111C13.6667 3.81643 13.5263 3.53381 13.2762 3.32544C13.0262 3.11706 12.687 3 12.3334 3H11.6667",
      stroke: "var(--lt-icon-primary)",
      strokeWidth: "1.25",
      strokeLinecap: "round",
      strokeLinejoin: "round"
    })]
  });
};

/** Objects/arrays — and strings holding one — pretty-print as a block;
 * everything else renders verbatim ("0" / "false" are real values). */
function toDisplay(value) {
  if (value === null || value === undefined) {
    return {
      text: 'null',
      block: false
    };
  }
  if (typeof value === 'string') {
    var trimmed = value.trim();
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      try {
        var parsed = JSON.parse(trimmed);
        if (parsed !== null && _typeof(parsed) === 'object') {
          return {
            text: JSON.stringify(parsed, null, 2),
            block: true
          };
        }
      } catch (_unused) {
        // A plain string that merely starts with a bracket.
      }
    }
    return {
      text: value,
      block: false
    };
  }
  if (_typeof(value) === 'object') {
    return {
      text: JSON.stringify(value, null, 2),
      block: true
    };
  }
  return {
    text: String(value),
    block: false
  };
}

/** kaneai SessionVariableTable port: {x} glyph + info-blue name, the value,
 * and a copy button revealed on row hover or keyboard focus. */
function VariablesTable(_ref) {
  var variables = _ref.variables;
  var rows = useMemo(function () {
    return variables.map(function (v, i) {
      var _toDisplay = toDisplay(v.value),
        text = _toDisplay.text,
        block = _toDisplay.block;
      var isNull = v.value === null || v.value === undefined;
      return {
        id: "".concat(v.name, "-").concat(i),
        name: /*#__PURE__*/_jsxs("span", {
          className: styles.varKey,
          children: [/*#__PURE__*/_jsx(VariableGlyph, {}), /*#__PURE__*/_jsx("span", {
            className: styles.varName,
            title: v.name,
            children: v.name
          })]
        }),
        value: /*#__PURE__*/_jsxs("span", {
          className: styles.varValue,
          children: [block ? /*#__PURE__*/_jsx("pre", {
            className: styles.varValueJson,
            children: text
          }) : /*#__PURE__*/_jsx("span", {
            className: isNull ? styles.varValueNull : styles.varValueText,
            children: text
          }), !isNull && /*#__PURE__*/_jsx("span", {
            className: styles.varCopy,
            children: /*#__PURE__*/_jsx(LTCopyToClipboard, {
              textToCopy: typeof v.value === 'string' ? v.value : text,
              copyAriaLabel: "Copy value of ".concat(v.name),
              dataTestId: "evidence-variable-copy"
            })
          })]
        })
      };
    });
  }, [variables]);
  return /*#__PURE__*/_jsx(KeyValueTable, {
    rows: rows,
    className: styles.varTable,
    keyCellClassName: styles.varKeyCell,
    valueCellClassName: styles.varValueCell
  });
}

/**
 * kaneai ViewSnippetAndVariables port (ScriptInstructionV4 /
 * DatabaseInstructionV4 modals): one modal, a code tab when there is code
 * and a Variables tab when the step set any. The code tab reuses the
 * viewer's terminal pane instead of kaneai's editor.
 */
export function SnippetVariablesModal(_ref2) {
  var _tabs$0$key, _tabs$;
  var title = _ref2.title,
    code = _ref2.code,
    _ref2$codeTabLabel = _ref2.codeTabLabel,
    codeTabLabel = _ref2$codeTabLabel === void 0 ? 'JS Snippet' : _ref2$codeTabLabel,
    _ref2$error = _ref2.error,
    error = _ref2$error === void 0 ? null : _ref2$error,
    variables = _ref2.variables,
    initialTab = _ref2.initialTab,
    onClose = _ref2.onClose;
  var baseId = useId();
  var tabs = [].concat(_toConsumableArray(code ? [{
    key: 'code',
    label: codeTabLabel
  }] : []), _toConsumableArray(variables.length > 0 ? [{
    key: 'variables',
    label: 'Variables'
  }] : []));
  var _useState = useState(tabs.some(function (t) {
      return t.key === initialTab;
    }) ? initialTab : (_tabs$0$key = (_tabs$ = tabs[0]) === null || _tabs$ === void 0 ? void 0 : _tabs$.key) !== null && _tabs$0$key !== void 0 ? _tabs$0$key : initialTab),
    _useState2 = _slicedToArray(_useState, 2),
    tab = _useState2[0],
    setTab = _useState2[1];
  var panelId = "".concat(baseId, "-panel");
  return /*#__PURE__*/_jsx(LTModal, {
    isOpen: true,
    ariaLabel: title,
    onClose: onClose,
    customHeaderComponent: /*#__PURE__*/_jsx("div", {
      className: styles.snippetModalHeader,
      children: /*#__PURE__*/_jsx("span", {
        className: styles.snippetModalTitle,
        children: title
      })
    }),
    extraStyle: {
      width: '960px',
      maxWidth: '92vw',
      maxHeight: '86vh',
      overflow: 'hidden',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif"
    },
    children: /*#__PURE__*/_jsxs("div", {
      className: styles.snippetModalBody,
      children: [/*#__PURE__*/_jsx(LTUnderlineNav
      // The lib types omit ariaLabel and demand Primer's children
      // prop; the runtime reads the former and ignores the latter.
      , {
        ariaLabel: "".concat(title, " sections"),
        children: null,
        ariaCurrent: "true",
        navs: tabs.map(function (t) {
          return {
            id: "".concat(baseId, "-").concat(t.key),
            key: t.key,
            label: t.label,
            contentId: panelId,
            isSelected: t.key === tab,
            dataTestId: "evidence-snippet-tab-".concat(t.key)
          };
        }),
        onClick: function onClick(_ref3) {
          var key = _ref3.key;
          return setTab(key);
        }
      }), /*#__PURE__*/_jsxs("div", {
        id: panelId,
        role: "tabpanel",
        "aria-labelledby": "".concat(baseId, "-").concat(tab),
        className: styles.snippetModalPanel,
        children: [tab === 'code' && code && /*#__PURE__*/_jsxs(_Fragment, {
          children: [/*#__PURE__*/_jsx(ScriptTerminal, {
            script: code
          }), error && /*#__PURE__*/_jsxs("div", {
            className: styles.snippetError,
            children: [error.line !== null && /*#__PURE__*/_jsxs("span", {
              className: styles.snippetErrorLine,
              children: ["Error at line ", error.line, ":"]
            }), /*#__PURE__*/_jsx("span", {
              children: error.message
            })]
          })]
        }), tab === 'variables' && /*#__PURE__*/_jsx(VariablesTable, {
          variables: variables
        })]
      })]
    })
  });
}